import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Settings, Sparkles, Users, LayoutDashboard, Gavel, FileText, Receipt, MessageSquare, Shield, ScrollText } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import SandboxBanner from "@/components/SandboxBanner";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const ACTIVE_SCHEME_KEY = "opstrai.active_scheme_id";

export function getActiveSchemeId() {
  return localStorage.getItem(ACTIVE_SCHEME_KEY);
}

export function setActiveSchemeId(id) {
  if (id) localStorage.setItem(ACTIVE_SCHEME_KEY, id);
  else localStorage.removeItem(ACTIVE_SCHEME_KEY);
}

export default function AppShell({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [schemes, setSchemes] = useState([]);
  const [activeId, setActiveId] = useState(getActiveSchemeId());

  // UX polish sprint — user reported "top bar doesn't show the page".
  // Derive a title from the current route as a fallback when the caller doesn't
  // pass one explicitly. Keeps AppShell adoption zero-friction across pages.
  const routeTitle = (() => {
    if (title) return title;
    const p = location.pathname;
    if (p.startsWith("/dashboard")) return "Committee dashboard";
    if (p.startsWith("/scheme/settings")) return "Scheme settings";
    if (p.startsWith("/scheme/bylaws")) return "By-laws";
    if (p.startsWith("/scheme/meetings")) return "Meetings";
    if (p.startsWith("/scheme/levies")) return "Levies";
    if (p.startsWith("/scheme/compliance")) return "Compliance";
    if (p.startsWith("/scheme/documents")) return "Documents";
    if (p.startsWith("/scheme/messages")) return "Messages";
    if (p.startsWith("/scheme/insurance")) return "Insurance";
    if (p.startsWith("/scheme/messaging/email")) return "Email inbox";
    if (p.startsWith("/scheme/emails")) return "Email inbox";
    if (p.startsWith("/scheme/ai")) return "AI Q&A";
    if (p.startsWith("/portfolio")) return "Portfolio";
    if (p.startsWith("/admin/home")) return "Platform health";
    if (p.startsWith("/admin/connectors")) return "Connectors";
    if (p.startsWith("/admin/jurisdictions")) return "Jurisdictions";
    if (p.startsWith("/admin/legislation-versions")) return "Legislation versions";
    if (p.startsWith("/admin/emails")) return "Email log";
    if (p.startsWith("/admin/scheduler")) return "Scheduler";
    if (p.startsWith("/owner")) return "Owner portal";
    if (p.startsWith("/onboarding")) return "Onboarding";
    return null;
  })();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/schemes");
        if (!mounted) return;
        setSchemes(data);
        if (data.length === 0) {
          // Platform admins can operate without a scheme — render the shell so
          // nav-admin is reachable.
          if (user?.system_role === "platform_admin") return;
          if (location.pathname !== "/onboarding") navigate("/onboarding", { replace: true });
          return;
        }
        // Ensure activeId is valid
        const ids = data.map((s) => s.scheme_id);
        let chosen = getActiveSchemeId();
        if (!chosen || !ids.includes(chosen)) {
          chosen = data[0].scheme_id;
          setActiveSchemeId(chosen);
        }
        setActiveId(chosen);
      } catch (_) {
        // ignore — user might not be ready
      }
    })();
    return () => {
      mounted = false;
    };
  }, [navigate, location.pathname, user?.system_role]);

  const active = schemes.find((s) => s.scheme_id === activeId);
  const isChair = active?.my_role === "chair" || user?.system_role === "platform_admin";
  const isPlatformAdmin = user?.system_role === "platform_admin";
  const isOwner = active?.my_role === "owner";

  // Owner UX Polish — for owners we only surface the By-laws nav tab if the
  // scheme actually has by-laws. Committee/chair see it regardless because
  // they can create them. Fires once per activeId change.
  const [bylawsCount, setBylawsCount] = useState(null);
  useEffect(() => {
    let live = true;
    if (!activeId || !isOwner) { setBylawsCount(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/schemes/${activeId}/bylaws`);
        if (live) setBylawsCount(Array.isArray(data) ? data.length : (data?.bylaws?.length ?? 0));
      } catch { if (live) setBylawsCount(0); }
    })();
    return () => { live = false; };
  }, [activeId, isOwner]);
  const showBylawsNav = !isOwner || (bylawsCount === null) || (bylawsCount > 0);

  const handleSwitch = (id) => {
    setActiveSchemeId(id);
    setActiveId(id);
    navigate("/dashboard");
  };

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-shell">
      <SandboxBanner />
      <header
        className="border-b bg-white sticky top-0 z-30"
        style={{ borderColor: "var(--border)" }}
      >
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/dashboard" data-testid="shell-wordmark-link">
              <Wordmark size={22} />
            </Link>
            {active && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border hover:bg-slate-50 transition-colors"
                    style={{
                      borderColor: "var(--border)",
                      color: "var(--navy)",
                    }}
                    data-testid="scheme-switcher-trigger"
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: "var(--blue)" }}
                    />
                    <span className="font-semibold">{active.name}</span>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                      style={{
                        background: "var(--blue-light)",
                        color: "var(--blue-dark)",
                      }}
                    >
                      {active.state}
                    </span>
                    {schemes.length > 1 && <ChevronDown size={14} />}
                  </button>
                </DropdownMenuTrigger>
                {schemes.length > 1 && (
                  <DropdownMenuContent align="start">
                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                      Your schemes
                    </DropdownMenuLabel>
                    {schemes.map((s) => (
                      <DropdownMenuItem
                        key={s.scheme_id}
                        onClick={() => handleSwitch(s.scheme_id)}
                        data-testid={`scheme-option-${s.scheme_id}`}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{s.name}</span>
                          <span className="text-xs" style={{ color: "var(--slate)" }}>
                            {s.state} · {s.my_role}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                )}
              </DropdownMenu>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!active && isPlatformAdmin && (
              <nav className="hidden md:flex items-center gap-1 mr-2" data-testid="admin-nav">
                <AdminMenu />
              </nav>
            )}
            {active && (
              <nav className="hidden md:flex items-center gap-1 mr-2">
                <Link to="/dashboard">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-medium"
                    data-testid="nav-dashboard"
                  >
                    <LayoutDashboard size={14} className="mr-1.5" />
                    Dashboard
                  </Button>
                </Link>
                <Link to="/scheme/members">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 font-medium"
                    data-testid="nav-members"
                  >
                    <Users size={14} className="mr-1.5" />
                    Members
                  </Button>
                </Link>
                <Link to="/scheme/meetings">
                  <Button variant="ghost" size="sm" className="h-8 font-medium" data-testid="nav-meetings">
                    <Gavel size={14} className="mr-1.5" />
                    Meetings
                  </Button>
                </Link>
                <Link to="/scheme/documents">
                  <Button variant="ghost" size="sm" className="h-8 font-medium" data-testid="nav-documents">
                    <FileText size={14} className="mr-1.5" />
                    Documents
                  </Button>
                </Link>
                <Link to="/scheme/levies">
                  <Button variant="ghost" size="sm" className="h-8 font-medium" data-testid="nav-levies">
                    <Receipt size={14} className="mr-1.5" />
                    Levies
                  </Button>
                </Link>
                <Link to="/scheme/messages">
                  <Button variant="ghost" size="sm" className="h-8 font-medium" data-testid="nav-messages">
                    <MessageSquare size={14} className="mr-1.5" />
                    Messages
                  </Button>
                </Link>
                <Link to="/scheme/insurance">
                  <Button variant="ghost" size="sm" className="h-8 font-medium" data-testid="nav-insurance">
                    <Shield size={14} className="mr-1.5" />
                    Insurance
                  </Button>
                </Link>
                <Link to="/scheme/compliance">
                  <Button variant="ghost" size="sm" className="h-8 font-medium" data-testid="nav-compliance">
                    <Sparkles size={14} className="mr-1.5" />
                    Compliance
                  </Button>
                </Link>
                {showBylawsNav && (
                  <Link to="/scheme/bylaws">
                    <Button variant="ghost" size="sm" className="h-8 font-medium" data-testid="nav-bylaws">
                      <ScrollText size={14} className="mr-1.5" />
                      By-laws
                    </Button>
                  </Link>
                )}
                {user?.system_role === "platform_admin" && (
                  <AdminMenu />
                )}
                {isChair && (
                  <Link to="/scheme/settings">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 font-medium"
                      data-testid="nav-settings"
                    >
                      <Settings size={14} className="mr-1.5" />
                      Settings
                    </Button>
                  </Link>
                )}
              </nav>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border hover:bg-slate-50"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="user-menu-trigger"
                >
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold"
                    style={{
                      background: "var(--navy)",
                      color: "white",
                    }}
                  >
                    {initials}
                  </span>
                  <span
                    className="hidden sm:inline text-sm font-medium"
                    style={{ color: "var(--navy)" }}
                  >
                    {user?.name || user?.email}
                  </span>
                  <ChevronDown size={14} style={{ color: "var(--slate)" }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user?.name}</div>
                  <div className="text-xs font-normal" style={{ color: "var(--slate)" }}>
                    {user?.email}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  data-testid="logout-btn"
                >
                  <LogOut size={14} className="mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* UX polish sprint — page title strip. User reported "top bar doesn't
          show the page". Rendered as a slim subheader below the sticky nav so
          existing layouts aren't destabilised. Shows page title + active scheme
          for authenticated pages. */}
      {routeTitle && (
        <div
          className="border-b bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="shell-title-strip"
        >
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-3 flex items-center gap-2 text-sm">
            <span className="font-semibold" style={{ color: "var(--navy)" }} data-testid="shell-page-title">
              {routeTitle}
            </span>
            {active && (
              <>
                <span className="text-slate-400">·</span>
                <span className="text-slate-600" data-testid="shell-page-scheme">
                  {active.name}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      <main className="flex-1">{children}</main>

      <footer
        className="border-t bg-white py-4"
        style={{ borderColor: "var(--border)" }}
      >
        <div
          className="max-w-6xl mx-auto px-5 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px]"
          style={{ color: "var(--tagline-grey)" }}
        >
          <span className="opstrai-tagline" style={{ fontSize: "0.6rem" }}>
            Strata operations, powered by AI
          </span>
          <div className="flex items-center gap-4">
            <Link to="/demo" className="hover:underline" data-testid="footer-demo-link">
              Demo tools
            </Link>
            <span className="mono">v0.2.0-phase1</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Sprint 2H Phase C — Consolidated admin nav. Uses shadcn DropdownMenu already
// imported for the scheme picker so no new imports required.
function AdminMenu() {
  const items = [
    { label: "Compliance templates", to: "/admin/compliance/templates" },
    { label: "Jurisdictions",        to: "/admin/jurisdictions" },
    { label: "Legislation versions", to: "/admin/legislation-versions" },
    { label: "Connectors",           to: "/admin/connectors" },
    { label: "Email log",            to: "/admin/email-log" },
    { label: "Scheduler",            to: "/admin/scheduler" },
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 font-medium" data-testid="nav-admin">
          <Settings size={14} className="mr-1.5" />
          Admin
          <ChevronDown size={12} className="ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
          Platform admin
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((it) => (
          <DropdownMenuItem key={it.to} asChild>
            <Link to={it.to} data-testid={`admin-menu-${it.to.split("/").pop()}`}>
              {it.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

